import { MSG } from '../../contracts/index';

/**
 * Derives the origin from the iframe's current src URL.
 * Returns empty string when src is not yet set (iframe still at parent origin).
 */
export function getIframeOrigin(iframe: HTMLIFrameElement | null): string {
  if (!iframe || !iframe.src) return '';
  try {
    return new URL(iframe.src).origin;
  } catch (_) {
    return '';
  }
}

/**
 * True when the iframe has a reachable contentWindow that has navigated to
 * (or past) its `src` origin. Before navigation completes, `about:blank`
 * inherits the host page origin (e.g. CareStack / Denticon), so postMessage
 * with the product `targetOrigin` throws / logs a DOMException flood.
 *
 * @param iframe - Target iframe element (or a test double with src/contentWindow).
 * @returns Whether it is safe to postMessage with {@link getIframeOrigin}'s origin.
 */
export function isIframeReady(iframe: HTMLIFrameElement | null): boolean {
  if (!iframe || !iframe.contentWindow) return false;
  const targetOrigin = getIframeOrigin(iframe);
  if (!targetOrigin) return false;
  try {
    const currentOrigin = iframe.contentWindow.location.origin;
    return currentOrigin === targetOrigin;
  } catch (_) {
    // Cross-origin: browsing context left the host page — safe to target src origin.
    return true;
  }
}

export function sendAuthToIframe(
  iframe: HTMLIFrameElement | null,
  payload: Record<string, unknown>,
): void {
  const origin = getIframeOrigin(iframe);
  if (!origin || !iframe!.contentWindow || !isIframeReady(iframe)) return;
  try {
    iframe!.contentWindow!.postMessage(
      { type: MSG.EMBEDDED, source: 'webextension', ...payload },
      origin,
    );
  } catch (_) {
    /* cross-origin or missing contentWindow */
  }
}
